// Core
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useDispatch } from 'react-redux';

// Components
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import AccountCircle from '@material-ui/icons/AccountCircle';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Button from '@material-ui/core/Button';
import { Typography } from '@material-ui/core';

// Data
import { AUTHORIZATION, NAVIGATION, TITLE } from '../constants/navigation';
import utils from '../utils';
import { setEmail as setEmailForSS } from '../store/reducers/global';

// Styles
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Spinner from '../assets/spinner.svg';
import Unauthorized from '../assets/401-unauthorized.svg';
import {
  SERVER_HOST,
  SEND_VERIFICATION_CODE_ENDPOINT,
  AUTH_LOGS_ENDPOINT,
} from '../constants/endpoints';
import { departments } from '../constants/volunteers';
import { AuthenticationContext } from '../hooks/global/AuthenticationContext';
import { AuthenticationProvider } from '../hooks/global/AuthenticationProvider';

const useStyles = makeStyles((theme) => ({
  margin: {
    margin: theme.spacing(1),
  },
  root: {
    '& > *': {
      margin: theme.spacing(1),
    },
  },
}));

const Authorization = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('xs'));
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [IPAddress, setIPAddress] = useState('');
  const [isLoading, setLoadingStatus] = useState(false);
  const [isCodeInput, setCodeInput] = useState(false);
  const { setAuthentication } = useContext(AuthenticationContext);
  const [serverVerificationCode, setServerVerificationCode] = useState(null);
  const codeRef = useRef();

  useEffect(() => {
    const fetchIPAddress = async () => {
      await fetch('https://api.ipify.org', {
        method: 'GET',
      })
        .then((res) => res.text())
        .then((data) => setIPAddress(data));
    };

    fetchIPAddress();
  }, []);

  const sendLogToDB = async ({ isSuccessfulAuthentication, error }) => {
    return fetch(SERVER_HOST + AUTH_LOGS_ENDPOINT, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        // TODO: We may define this automatically using hostname
        mode: 'production',
        email: email,
        IPAddress: IPAddress,
        isSuccessfulAuthentication: isSuccessfulAuthentication,
        errorText: error,
        credentialsHolder: departments,
      }),
    })
      .then((response) => response.json())
      .then((result) => console.log(result))
      .catch((_error) => console.log(_error));
  };

  const checkUserEmail = async () => {
    setLoadingStatus(true);

    return fetch(SERVER_HOST + SEND_VERIFICATION_CODE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email,
        systemId: 'admin',
      }),
    })
      .then(async (response) => {
        if (response.ok) {
          const _response = await response.json();
          const serverCode = String(_response.data);

          sendLogToDB({
            isSuccessfulAuthentication: true,
            error: null,
          });
          setCodeInput(true);
          setServerVerificationCode(serverCode);
        } else {
          const errorText = 'Введены неверные данные';

          setLoadingStatus(false);
          sendLogToDB({
            isSuccessfulAuthentication: false,
            error: errorText,
          });

          return setError(errorText);
        }
      })
      .catch(() => {
        return setError('Введены неверные данные');
      })
      .finally(() => {
        setLoadingStatus(false);
      });
  };

  const checkVerificationCode = (serverVerificationCode) => {
    const _userVerificationCode = codeRef.current.value;
    const isRightCode = _userVerificationCode === serverVerificationCode;

    if (isRightCode) {
      dispatch(
        setEmailForSS({
          email: email,
        })
      );

      setAuthentication(true);
    } else {
      return setError('Код не совпадает с отправленным');
    }
  };

  return (
    <AuthenticationProvider>
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <img alt="Иконка загрузки" src={Spinner} />
        </div>
      ) : (
        <div
          style={{
            display: isMobileDevice ? 'block' : 'flex',
            justifyContent: 'space-around',
            minHeight: 'calc(100vh - 120px)',
          }}
        >
          {error && (
            <div
              style={{
                display: 'flex',
                justifyContent: isMobileDevice && 'center',
                alignItems: 'center',
              }}
            >
              <img
                alt="Изображение ошибки авторизации"
                src={Unauthorized}
                style={{ width: isMobileDevice ? '80%' : '350px' }}
              />
            </div>
          )}
          <div
            style={{ display: !isMobileDevice && 'flex', alignItems: 'center' }}
          >
            <div style={{ marginTop: '20px' }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {utils.searchByConstant(NAVIGATION, AUTHORIZATION, TITLE)}
              </Typography>
              <div className={classes.margin}>
                <Grid container spacing={1} alignItems="flex-end">
                  <Grid item>
                    <AccountCircle />
                  </Grid>
                  <Grid item>
                    <TextField
                      id="input-with-icon-grid-1"
                      label="Ваша эл. почта"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      style={{ minWidth: isMobileDevice && '60vw' }}
                    />
                  </Grid>
                </Grid>
              </div>
              {isCodeInput && (
                <div className={classes.margin}>
                  <Grid container spacing={1} alignItems="flex-end">
                    <Grid item>
                      <VpnKeyIcon />
                    </Grid>
                    <Grid item>
                      <TextField
                        inputRef={codeRef}
                        id="input-with-icon-grid-1"
                        label="Код из email"
                        value={code}
                        onChange={(event) => setCode(event.target.value)}
                        style={{ minWidth: isMobileDevice && '60vw' }}
                      />
                    </Grid>
                  </Grid>
                </div>
              )}
              <Box mt={3} display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => {
                    if (isCodeInput) {
                      checkVerificationCode(serverVerificationCode);
                    } else {
                      checkUserEmail();
                    }
                  }}
                >
                  Войти
                </Button>
              </Box>
              {error && (
                <Box mt={1}>
                  <Typography
                    variant="body2"
                    component="body2"
                    color="secondary"
                    gutterBottom
                  >
                    {error}
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </div>
      )}
    </AuthenticationProvider>
  );
};

export default Authorization;
